import * as Sentry from "@sentry/react";
import { createBrowserHistory, History } from "history";

interface ISentryContext {
    [source: string]: string;
}

type Environment = "Development" | "Stage" | "Test" | "Production";

const getEnvironment = (): Environment => {
    switch (window.location.host.toLowerCase()) {
        // Production
        case "lifedirect.co.nz":
        case "www.lifedirect.co.nz":
            return "Production";
        // Stage
        case "stage.lifedirect.co.nz":
            return "Stage";
        // Test
        case "test.lifedirect.co.nz":
            return "Test";
        // Dev/Default
        case "dev.lifedirect.co.nz":
        default:
            return "Development";
    }
};

const UserEnvironment = getEnvironment();

interface IUseErrorHandler {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Log: (err: any, context: ISentryContext) => void;
    SetUserData: (groupId: string) => void;
}

const useErrorLogger = (): IUseErrorHandler => {
    // This allows us to take
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Log = (err: any, context: ISentryContext): void => {
        Sentry.setContext("Multi-compare", context);
        Sentry.captureException(err);

        if (UserEnvironment !== "Production") {
            // eslint-disable-next-line no-console
            console.error(err);
        }
    };

    const SetUserData = (groupId: string): void => {
        Sentry.setUser({
            groupId
        });
    };

    return {
        Log,
        SetUserData
    };
};

export const SetUpErrorLogging = (): History => {
    const history = createBrowserHistory({
        basename: "/compare"
    });

    Sentry.init({
        dsn: "https://53d9e2e36d3e400f95ccd13e1ce41c63@o370343.ingest.sentry.io/5179635",
        environment: UserEnvironment,
        integrations: [
            // Tracking the routes in Sentry.
            Sentry.reactRouterV5BrowserTracingIntegration({
                history
            }),
        ],
        // Ideally a version number, however this allows us to only look at issues coming from Multi-compare,
        // unless we add a new project (and new DSN)
        release: "Multi-compare",
        // https://docs.sentry.io/platforms/javascript/configuration/sampling/
        // Number between 0 & 1 - Percentage of transactions that get recorded and sent to sentry.
        // Not recommended to be 1 in prod, to reduce load and volume of errors.
        // We have a small enough volume, 1 might make the most sense.
        tracesSampleRate: 1.0
    });

    return history;
};

export default useErrorLogger;
